import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import lbDate from 'lbdate';
import { from, NEVER, Observable, throwError } from 'rxjs';
import { catchError, debounceTime, map, tap } from 'rxjs/operators';
import { BlobResponse, ContentAndStatus } from '../Models/OtherClasses';
import { ConfigService } from "./ConfigService";
import { DialogService } from "./DialogService";

@Injectable()
export class BaseService {

	getDateStr(date: Date): string {
		return this.configData.getDateStr(date);

	}

	constructor(private _http: HttpClient,
		private modalService: DialogService,
		public configData: ConfigService) { }

	showError(msg: string, hideErrorAlert: boolean) {
		return (error: any, caught: any): Observable<never> => {
			if (hideErrorAlert) {
				return from(NEVER);
			}
			if (error) {
				if (error.status == 400) {
				} else if (error.status == 0) {
					this.modalService.error('Site is temporarily disconnected. Please try again in just a minute', '');

				} else if (error.status == 401) {
					this.modalService.showLogInError();
					error = 'Sign In Needed';
				} else {
					var myErrorObj = error.error;
					if ((error.status != 400 && error.status != 404) || (myErrorObj && !myErrorObj.errors)) {
						if (myErrorObj && myErrorObj.detail) {
							if (myErrorObj.detail.indexOf("Failed:") >= 0) {
								//don't do anything error as this should be handled by the model state displays
							} else {
								let bodyMsg: string = "";
								if (myErrorObj.exception?.details) {
									bodyMsg += "<br />" + myErrorObj.exception.details;
								}
								if (bodyMsg) {
									bodyMsg = this.configData.TranslateWords.IfThisContinuesSendDevelopers +
										' : <br /><br /><br />' + bodyMsg;
								}
								this.modalService.error(myErrorObj.detail, bodyMsg);
							}
						} else {
							var cache: any[] = [];
							if (!this.configData.isSuperAdmin()) {
								this.modalService.error(msg, '');
							} else {
								this.modalService.error(msg, this.configData.TranslateWords.IfThisContinuesSendDevelopers +
									' : <br /><br /><br />' + JSON.stringify(error, function (key, value) {
										if (typeof value === 'object' && value !== null) {
											if (cache.indexOf(value) !== -1) {
												// Circular reference found, discard key
												return;
											}
											// Store value in our collection
											cache.push(value);
										}
										return value;
									}));
							}
							cache = null; // Enable garbage collection
						}
					}
				}
			} else {
				this.modalService.error(msg, '');
			}
			return this.myThrowError(error);
		};
	}

	myThrowError(error: any): Observable<never> {
		if (error) {
			return throwError(error);
		} else {
			return throwError('Server error');
		}
	}

	loadAll(baseUrl: string): Observable<any> {
		return this.getPlain(baseUrl, this.configData.TranslateWords.FailedToLoadItems);
	}

	getEdit(id: System.Guid, baseUrl: string): Observable<any> {
		return this.get(id, baseUrl + '/Edit');
	}

	get(id: System.Guid, baseUrl: string): Observable<any> {
		return this.getWithParams(id, baseUrl, "", false);
	}

	getWOError(id: System.Guid, baseUrl: string): Observable<any> {
		return this.getWithParams(id, baseUrl, "", true);
	}

	getWithParams(id: System.Guid, baseUrl: string, parameters: string, hideErrorAlert: boolean): Observable<any> {
		id = id || this.configData.DefaultEmptyGuid;
		return this.rawWithParams(id, baseUrl, parameters, hideErrorAlert);
	}

	getPlain(url: string, errorMessage: string): Observable<any> {
		return this.getPlainErrorOption(url, errorMessage, false);
	}
	getPlainErrorOption(url: string, errorMessage: string, hideErrorAlert: boolean): Observable<any> {
		return this.getPlainErrorOptionWithStatusCode(url, errorMessage, hideErrorAlert)
			.pipe(
				map(resp => {
					return resp.content;
				}),
				catchError(this.showError(errorMessage, hideErrorAlert))
			);
	}

	getPlainErrorOptionWithStatusCode(url: string, errorMessage: string, hideErrorAlert: boolean): Observable<ContentAndStatus<any>> {
		return this._http.get<any>(url, { observe: "response" })
			.pipe(
				tap((res) => {
					if (res && res.headers) {
						var version = res.headers.get("serverVersion");
						if (version && this.configData.shouldShowVersionAlert(version)) {
							this.modalService.message(this.configData.TranslateWords.NewVersion, 'Version');
						}
					}
				}),
				map(res => {
					return {
						headers: res.headers,
						content: res.body,
						status: res.status,
					};
				})
			);
	}

	rawWithParams(id: System.Guid, baseUrl: string, parameters: string, hideErrorAlert: boolean): Observable<any> {
		return this.getPlainErrorOption(baseUrl + '/' + id + parameters, this.configData.TranslateWords.FailedToReload, hideErrorAlert);
	}

	save(tempItem: TooBig4Car.Logic.ObjectBase, baseUrl: string): Observable<any> {
		return this.post(tempItem, baseUrl, true);
	}

	postBlob(tempItem: any, baseUrl: string): Observable<BlobResponse> {
		let bodyString = this.innerStringify(tempItem); // Stringify payload

		return this._http.post(baseUrl, bodyString, { responseType: "blob", observe: "response" })
			.pipe(
				catchError(this.showError(this.configData.TranslateWords.Failed, false)),
				map(res => {
					var data = {
						blob: new Blob([res.body], { type: res.headers.get('Content-Type') }),
						fileName: this.getFileName(res.headers)
					} as BlobResponse;

					return data;
				}),
			);
	}
	loadBlob(baseUrl: string): Observable<BlobResponse> {

		return this._http.get(baseUrl, { responseType: "blob", observe: "response" })
			.pipe(
				catchError(this.showError(this.configData.TranslateWords.Failed, false)),
				map(res => {
					var data = {
						blob: new Blob([res.body], { type: res.headers.get('Content-Type') }),
						fileName: this.getFileName(res.headers)
					} as BlobResponse;

					return data;
				}),
			);
	}
	loadBlobAndHeaders(baseUrl: string): Observable<ContentAndStatus<BlobResponse>> {

		return this._http.get(baseUrl, { responseType: "blob", observe: "response" })
			.pipe(
				catchError(this.showError(this.configData.TranslateWords.Failed, false)),
				map(res => {
					var data = {
						blob: new Blob([res.body], { type: res.headers.get('Content-Type') }),
						fileName: this.getFileName(res.headers)
					} as BlobResponse;

					return {
						headers: res.headers,
						content: data,
						status: res.status
					};
				}),
			);
	}
	getFileName(headers: HttpHeaders): string {
		var name = headers.get('File-Name');
		if (!name) {
			name = headers.get('content-disposition');
			if (name) {
				name = name.substring(name.indexOf("filename=")).replace("filename=", "");
				if (name.indexOf(";") >= 0) {
					name = name.substring(0, name.indexOf(";")).replace(";", "");
				}
				name = name.replace('"', '').replace('"', '');
			}
		}
		return name.trim();
	}

	innerStringify(tempItem: any) {
		if (null != tempItem) {
			return lbDate().run(() => {
				return JSON.stringify(tempItem);
			});
			//JSON.stringify(tempItem, (key: string, value: any) => {
			//	if (value instanceof Date) {
			//		return this.configData.innerDateReplaceToJSON(value);
			//	} else {
			//		return value;
			//	}
			//})
		}
		return '';
	}
	post(tempItem: any, baseUrl: string, popUpError: boolean): Observable<any> {
		let bodyString = this.innerStringify(tempItem); // Stringify payload
		let options = { reportProgress: true }; // Create a request option

		return this._http.post(baseUrl, bodyString, options)
			.pipe(
				debounceTime(200),
				catchError(this.showError(this.configData.TranslateWords.Failed, !popUpError))
			);
	}

	postWithStatusCode(tempItem: any, baseUrl: string, popUpError: boolean): Observable<ContentAndStatus<any>> {
		let bodyString = this.innerStringify(tempItem); // Stringify payload

		return this._http.post(baseUrl, bodyString, {
			reportProgress: true,
			observe: "response"
		})
			.pipe(
				debounceTime(200),
				map(res => {
					return {
						headers: res.headers,
						content: res.body,
						status: res.status
					};
				}),
				catchError(this.showError(this.configData.TranslateWords.Failed, !popUpError))
			);
	}

	deleteItem(id: System.Guid, baseUrl: string): Observable<any> {
		return this.deleteWithParams(id, baseUrl, '');
	}

	deleteWithParams(id: System.Guid, baseUrl: string, parameters: string): Observable<any> {
		return this._http.delete(baseUrl + '/' + id + parameters, {})
			.pipe(
				catchError(this.showError(this.configData.TranslateWords.Failed, true))
			);
	}


}
